import { useMemo } from 'react'
import { useGlobalState } from '@highlight/ipc/react'
import type { CalendarEvent } from '@highlight/common'
import type { DayCalendarEvent } from '@renderer/components/calendar-day-view/calendar-day-layout'
import { useCalendars, getEffectiveCalendarColor, type CalendarColorValue } from '@renderer/hooks/use-calendars'

export type ColoredCalendarEvent = CalendarEvent & Pick<DayCalendarEvent, 'calendarName' | 'color'>

/** Synced events the user hasn't declined, coloured by their calendar. */
export function useDayCalendarEvents(): {
  events: Array<ColoredCalendarEvent>
  isLoading: boolean
  error: string | undefined
} {
  const [calendarEvents] = useGlobalState<Array<CalendarEvent>>('calendar.todaysEvents', [])
  const [eventsLoading] = useGlobalState<boolean>('calendar.todaysEventsLoading', false)
  const { calendars, isLoading, error } = useCalendars()

  const events = useMemo<Array<ColoredCalendarEvent>>(() => {
    const calendarsById = new Map(calendars?.map((calendar) => [calendar.id, calendar]) ?? [])
    return calendarEvents
      .filter((event) => !event.isCanceled && event.selfResponseStatus !== 'declined')
      .map((event) => {
        const calendar = event.calendarId ? calendarsById.get(event.calendarId) : undefined
        return {
          ...event,
          calendarName: calendar?.name,
          color: calendar
            ? getEffectiveCalendarColor(
                calendar.id,
                calendar.hexColor,
                calendar.metadata?.color as CalendarColorValue | undefined,
              )
            : undefined,
        }
      })
  }, [calendarEvents, calendars])

  return { events, isLoading: eventsLoading || isLoading, error }
}
